import { DOCUMENT, Location, formatDate } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { FormlyFieldConfig } from '@ngx-formly/core';
import * as fileSaver from 'file-saver';
import { BehaviorSubject, Observable, map } from 'rxjs';
import * as XLSX from 'xlsx';
import { AuthService } from '../auth/auth.service';

import { HttpClient } from '@angular/common/http';
import * as microsoftTeams from '@microsoft/teams-js';
import * as moment from 'moment';
import { MessageService } from 'primeng/api';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Table } from 'primeng/table';
import { LicenseService } from 'src/app/EBMSuite/services/license/license.service';
import { LayoutService } from 'src/app/layout/service/app.layout.service';
import { ProjectConstants } from 'src/assets/constants/project.constants';
import { environment } from 'src/environments/environment';
import { ISibasiHTTPResponse } from '../../model/sibasiHTTPResponse.model';
import { formatDate as dateFormatter, isEmpty } from '../../utils';

@Injectable({
  providedIn: 'root'
})
export class UtilService {
  previousURL = '';
  currentURL = '';
  deviceFingerPrint = '';
  projectConstants = ProjectConstants;
  isManager!: boolean | null;
  isManagerString = localStorage.getItem('isManager');

  fullDays = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];
  months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  private messageSubject = new BehaviorSubject<string | null>(null);
  message$ = this.messageSubject.asObservable();

  titleService = inject(Title);
  _document = inject(DOCUMENT);
  router = inject(Router);
  location = inject(Location);
  authService = inject(AuthService);
  httpClient = inject(HttpClient);
  messageService = inject(MessageService);
  layoutService = inject(LayoutService);
  licenseService = inject(LicenseService);

  passwordsMustMatch(passwordKey: string, passwordConfirmationKey: string) {
    return (group: FormGroup) => {
      const passwordInput = group.controls[passwordKey],
        passwordConfirmationInput = group.controls[passwordConfirmationKey];
      if (passwordInput.value !== passwordConfirmationInput.value) {
        return passwordConfirmationInput.setErrors({
          notEquivalent: true
        });
      } else {
        return passwordConfirmationInput.setErrors(null);
      }
    };
  }

  userTheme: boolean | undefined;
  setDocTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  setDocFavicon(): void {
    const faviconId = 'appFavicon';
    const faviconDefaultPath = 'assets/img/brand';
    const favicon = ProjectConstants.favicon;

    const faviconElement = this._document.getElementById(faviconId);
    if (faviconElement && favicon) {
      faviconElement.setAttribute('href', `${faviconDefaultPath}/${favicon}`);
    }
  }

  createInitials(name: any): string {
    let initials = '';

    if (name) {
      for (let i = 0; i < name.length; i++) {
        if (name.charAt(i) === ' ') {
          continue;
        }
        if (name.charAt(i) === name.charAt(i).toUpperCase()) {
          initials += name.charAt(i);

          if (initials.length == 2) {
            break;
          }
        }
      }
    }
    return initials;
  }

  formatDate(dateTimeStamp: string): string {
    const now = new Date(dateFormatter(new Date())),
      dt = new Date(dateFormatter(new Date(dateTimeStamp))),
      diffTime = now.getTime() - dt.getTime(),
      diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)),
      diffYears = now.getFullYear() - dt.getFullYear();

    if (diffYears === 0 && diffDays === 0) {
      return 'Today';
    } else if (diffYears === 0 && diffDays === 1) {
      return 'Yesterday';
    } else if (diffYears === 0 && diffDays === -1) {
      return 'Tomorrow';
    } else if (diffYears === 0 && diffDays > -7 && diffDays < 0) {
      return this.fullDays[dt.getDay()];
    } else {
      return moment(dateTimeStamp).isValid()
        ? moment(dateTimeStamp).format('ddd, Do MMM YYYY')
        : '--';
    }
  }

  doesURLHaveText(text: string): boolean {
    const activeRoute = this.router.url;
    return activeRoute.includes(text) ? true : false;
  }

  goBack(dialogRef?: DynamicDialogRef): void {
    if (dialogRef) dialogRef.destroy();
    this.location.back();
  }

  checkIfStringIsADate(date: any): boolean {
    if (isEmpty(date)) return false;
    // Attempt to parse the date
    const parsedDate = new Date(date);

    // Check if the parsed date is valid
    const isValidDate = !isNaN(parsedDate.getTime());

    // Regular expression to match valid date strings
    const isDateString =
      /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z|\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4})$/.test(
        date
      );

    return isValidDate && isDateString;
  }

  getValue(obj: any, path?: string): string {
    if (!path) {
      return this.checkIfStringIsADate(obj) ? this.formatDate(obj) : obj;
    }

    if (!obj) {
      return '--';
    }

    const properties = path!.split('.');
    const nextProperty = properties.shift();

    if (typeof obj[nextProperty as keyof typeof obj] === 'undefined') {
      return '--';
    }

    return this.getValue(obj[nextProperty as keyof typeof obj], properties.join('.'));
  }

  public exportAsExcelFile(json: any[], excelFileName: string): void {
    const MAX_ROWS = 32767;
    const MAX_TEXT_LENGTH = 32767;
    const worksheets: XLSX.WorkSheet[] = [];
    let rowIndex = 0;

    while (rowIndex < json.length) {
      const chunk = json.slice(rowIndex, rowIndex + MAX_ROWS);
      const processedChunk = chunk?.map((row) => {
        const processedRow: any = {};
        Object.keys(row).forEach((key) => {
          let value = row[key];
          if (typeof value === 'string' && value.length > MAX_TEXT_LENGTH) {
            value = value.substring(0, MAX_TEXT_LENGTH);
          }
          processedRow[key] = value;
        });
        return processedRow;
      });
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(processedChunk);
      worksheets.push(worksheet);
      rowIndex += MAX_ROWS;
    }

    const workbook: XLSX.WorkBook = {
      Sheets: {},
      SheetNames: []
    };

    for (let i = 0; i < worksheets.length; i++) {
      workbook.SheetNames.push(`data_${i}`);
      workbook.Sheets[`data_${i}`] = worksheets[i];
    }

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });
    this.saveAsExcelFile(excelBuffer, excelFileName);
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    fileSaver.saveAs(
      data,
      fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION
    );
  }

  public toDateString(date: Date): string {
    return (
      date.getFullYear().toString() +
      '-' +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      '-' +
      ('0' + date.getDate()).slice(-2) +
      'T' +
      date.toTimeString().slice(0, 5)
    );
  }

  public removeTimeFromDate(date: string): string {
    return date.split('T')[0];
  }

  createImageFromBlob(image: Blob) {
    let imageToShow: any;
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        imageToShow = reader.result;
      },
      false
    );

    if (image) {
      reader.readAsDataURL(image);
    }

    return imageToShow;
  }

  getUserFirstName(): string {
    const decodedToken = this.authService.decodedToken();
    const firstName = decodedToken.firstname;
    return firstName ? firstName : '';
  }

  getUserEmail(): string {
    const decodedToken = this.authService.decodedToken();
    const authItem = decodedToken.authItem;
    if (authItem) {
      return authItem[0].value;
    }

    return '';
  }

  setCurrentCurrency(currency: string): void {
    localStorage.setItem('currency', currency);
  }

  getCurrentCurrency(): any {
    return localStorage.getItem('currency');
  }

  getUserId(): string {
    const decodedToken = this.authService.decodedToken();
    const userId = decodedToken.id;

    return userId;
  }
  getProfileColor(): string {
    const decodedToken = this.authService.decodedToken();
    const profileColor = decodedToken.profileBgColor;
    return profileColor;
  }

  populateDropdownField(
    keyNames: any[],
    formFieldsArray: FormlyFieldConfig[],
    service: Observable<ISibasiHTTPResponse<any>>
  ): any[] {
    for (let index = 0; index < formFieldsArray.length; index++) {
      const formlyField = formFieldsArray[index];

      if (keyNames.includes(formlyField.key) && formlyField.props) {
        formlyField.props.options = service.pipe(map((data) => data.result));
      }

      if (!formlyField.key && formlyField.fieldGroup) {
        this.populateDropdownField(keyNames, formlyField.fieldGroup, service);
      }

      formFieldsArray[index] = formlyField;
    }

    return formFieldsArray;
  }

  convertToSimpleDate(dateObj: Date): string {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onGlobalFilter(table: Table, event: Event) {
    table.filterGlobal((event.target as HTMLInputElement)?.value, 'contains');
  }
  getCurrentOrganization() {
    const currentOrganization = localStorage.getItem('organization');
    return currentOrganization;
  }

  CheckIfIsManager() {
    if (this.isManagerString !== null) {
      return (this.isManager = this.isManagerString === 'true');
    } else {
      return (this.isManager = false);
    }
  }
  formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.round(seconds % 60);

    const hoursString = hours > 0 ? hours + 'h ' : '';
    const minutesString = minutes > 0 ? minutes + 'm ' : '';
    const secondsString = remainingSeconds + 's';

    return hoursString + minutesString + secondsString;
  }

  detectBrowserName(): string {
    const agent = window.navigator.userAgent.toLowerCase();
    switch (true) {
      case agent.indexOf('edge') > -1:
        return 'edge';
      case agent.indexOf('opr') > -1 && !!(window as any).opr:
        return 'opera';
      case agent.indexOf('chrome') > -1 && !!(window as any).chrome:
        return 'chrome';
      case agent.indexOf('trident') > -1:
        return 'ie';
      case agent.indexOf('firefox') > -1:
        return 'firefox';
      case agent.indexOf('safari') > -1:
        return 'safari';
      default:
        return 'other';
    }
  }

  detectBrowserVersion(): string {
    const userAgent = navigator.userAgent;
    let tem,
      matchTest =
        userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) ||
        [];

    if (/trident/i.test(matchTest[1])) {
      tem = /\brv[ :]+(\d+)/g.exec(userAgent) || [];
      return 'IE ' + (tem[1] || '');
    }
    if (matchTest[1] === 'Chrome') {
      tem = userAgent.match(/\b(OPR|Edge)\/(\d+)/);
      if (tem != null) {
        return tem.slice(1).join(' ').replace('OPR', 'Opera');
      }
    }
    matchTest = matchTest[2]
      ? [matchTest[1], matchTest[2]]
      : [navigator.appName, navigator.appVersion, '-?'];
    if ((tem = userAgent.match(/version\/(\d+)/i)) != null) {
      matchTest.splice(1, 1, tem[1]);
    }
    return matchTest.join(' ');
  }

  stripHtmlTag: any = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  formatDataToPrimeDate(dateString: string | undefined): string {
    if (!dateString) return '';
    return formatDate(new Date(dateString), 'yyyy-MM-dd', 'en');
  }

  async checkForRole(roles?: string[]) {
    const decodedUser: any = this.authService.decodedToken();
    if (decodedUser.roles.includes('SYSADMIN')) {
      return true;
    } else {
      return roles ? roles?.includes(await this.checkForOrganizationRole()) : false;
    }
  }

  checkForOrganizationRole() {
    return new Promise<any>((resolve, reject) => {
      this.httpClient
        .get(`${environment.api_url}organization/member/roles/one`)
        .subscribe(
          (data: any) => {
            resolve(data.result.roleName);
          },
          (error) => {
            reject(error);
          }
        );
    });
  }

  convertDataToISOString(date: string): string {
    if (!date) return '';
    const formattedDate = new Date(date);
    const year = formattedDate.getFullYear();
    const month = ('0' + (formattedDate.getMonth() + 1)).slice(-2);
    const day = ('0' + formattedDate.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  convertMeetingDateTime(
    startUTC: string,
    startTime: string,
    endTime: string,
    showOnlyTime?: boolean
  ) {
    const startDate = new Date(startUTC);

    function isISODateTimeString(dateTimeString: string) {
      const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
      return isoRegex.test(dateTimeString);
    }

    function formatTime(isoString: string | number | Date) {
      const date = new Date(isoString);
      const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      };
      return date.toLocaleString('en-US', options);
    }

    if (isISODateTimeString(startTime)) {
      startTime = formatTime(startTime);
    }
    if (isISODateTimeString(endTime)) {
      endTime = formatTime(endTime);
    }

    function formatDate(date: Date) {
      const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      };
      return date.toLocaleDateString('en-US', options);
    }

    // Create the final string
    const formattedDate = formatDate(startDate);
    const finalString = `${
      showOnlyTime ? '' : formattedDate + ','
    } ${startTime} - ${endTime}`;
    return finalString;
  }

  addMessage(severity: string, summary: string, detail: string) {
    this.messageService.add({ severity, summary, detail });
  }

  formatBytes(bytes: number) {
    if (bytes < 1024) {
      return bytes.toFixed(1) + ' Bytes';
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    } else if (bytes < 1024 * 1024 * 1024) {
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    } else {
      return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    }
  }

  get containerClass() {
    return {
      'layout-light': this.layoutService.config().colorScheme === 'light',
      'layout-dark': this.layoutService.config().colorScheme === 'dark',
      'layout-overlay': this.layoutService.config().menuMode === 'overlay',
      'layout-static': this.layoutService.config().menuMode === 'static',
      'layout-slim': this.layoutService.config().menuMode === 'slim',
      'layout-slim-plus': this.layoutService.config().menuMode === 'slim-plus',
      'layout-horizontal': this.layoutService.config().menuMode === 'horizontal',
      'layout-reveal': this.layoutService.config().menuMode === 'reveal',
      'layout-drawer': this.layoutService.config().menuMode === 'drawer',
      'layout-static-inactive':
        this.layoutService.state.staticMenuDesktopInactive &&
        this.layoutService.config().menuMode === 'static',
      'layout-overlay-active': this.layoutService.state.overlayMenuActive,
      'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
      'p-ripple-disabled': !this.layoutService.config().ripple,
      'layout-sidebar-active': this.layoutService.state.sidebarActive,
      'layout-sidebar-anchored': this.layoutService.state.anchored
    };
  }

  // **************TO BE DISCUSSED***************************

  padTo2Digits(num: any) {
    return num.toString().padStart(2, '0');
  }

  convertMsToTime(milliseconds: any) {
    let seconds = Math.floor(milliseconds / 1000);
    let minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    seconds = seconds % 60;
    minutes = minutes % 60;

    // 👇️ If you don't want to roll hours over, e.g. 24 to 00
    // 👇️ comment (or remove) the line below
    // commenting next line gets you `24:00:00` instead of `00:00:00`
    // or `36:15:31` instead of `12:15:31`, etc.
    // hours = hours % 24;

    return `${this.padTo2Digits(hours)}:${this.padTo2Digits(
      minutes
    )}:${this.padTo2Digits(seconds)}`;
  }

  calculateTimeInBetweenDates(startTime: string, endTime?: string): number {
    const startTimeFormatted = startTime ? new Date(startTime) : new Date();
    const endTimeFormatted = endTime ? new Date(endTime) : new Date();
    const timeBetweenInMilliseconds =
      endTimeFormatted.getTime() - startTimeFormatted.getTime();
    return timeBetweenInMilliseconds;
  }

  percentage(num: number, per: number): number {
    const percentValue = (num / 100) * per;
    const roundedOffValue = Number(percentValue.toFixed(2));
    return roundedOffValue;
  }

  calculateItemsTotalTax(items: any[]): number {
    let totalTax = 0;

    if (items?.length > 0) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.taxes && item.taxes.length > 0) {
          item.taxes.forEach((tax: any) => {
            const { percentage } = tax;
            const taxValue = this.percentage(item.itemPrice, percentage);
            totalTax += taxValue * item.quantity;
          });
        }
      }
    }

    return totalTax;
  }

  toSentenceCase(word: string): string {
    if (word) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
    return '';
  }

  getCurrentMonth(): string {
    const currentDate = new Date();
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    const currentMonthName = months[currentDate.getMonth()];
    return currentMonthName.toUpperCase();
  }

  showPermissionError(message: string) {
    this.messageSubject.next(message);
  }

  navigateToExternalURL(url: string, newWindow = false): void {
    if (newWindow) {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  }

  teamsOpenLinkOutsideApp(targetUrl: string): void {
    this.fallbackOpenLink(targetUrl);
    // if (typeof microsoftTeams !== 'undefined' && microsoftTeams.app) {
    //     microsoftTeams.app
    //         .openLink(targetUrl)
    //         .then(() => {
    //
    //         })
    //         .catch((error) => {
    //             console.error('Failed to open link with Teams SDK:', error);
    //             // Fallback to a default method
    //             this.fallbackOpenLink(targetUrl);
    //         });
    // } else {
    //     console.warn(
    //         'Microsoft Teams SDK is not available. Using fallback.'
    //     );
    //     // Fallback if SDK is not initialized or unavailable
    //     this.fallbackOpenLink(targetUrl);
    // }
  }

  // TEAMS UTIL SERVICES
  async isTeamsEnvironment(): Promise<boolean> {
    try {
      await microsoftTeams.app.initialize();
      const context = await microsoftTeams.app.getContext();
      return !!context;
    } catch (error) {
      console.warn('Failed to get Teams context:', error);
      return false;
    }
  }

  fallbackOpenLink(targetUrl: string): void {
    try {
      window.open(targetUrl, '_blank');
    } catch (error) {
      console.error('Failed to open link using fallback method:', error);
    }
  }
}
